import { useEffect, useRef, useState } from "react";
import { streamAI } from "../lib/api";
import { Sparkles, X, Send, MessageCircle } from "lucide-react";

export default function AIAssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm DealVest's AI advisor. Ask me anything — buying, selling, raising capital, valuation, or how the platform works." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => `assistant-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || streaming) return;
    const userMsg = { role: "user", text: input };
    setMessages(m => [...m, userMsg, { role: "assistant", text: "" }]);
    const prompt = input;
    setInput("");
    setStreaming(true);
    await streamAI("/ai/assistant", { message: prompt, session_id: sessionId },
      t => setMessages(m => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + t };
        return copy;
      }),
      () => setStreaming(false),
      () => setStreaming(false));
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="ai-assistant-toggle"
        aria-label="AI Assistant"
        className={`fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-blue-600 text-white shadow-xl shadow-blue-300 hover:bg-blue-700 flex items-center justify-center transition-transform ${open ? "scale-90" : "hover:scale-105 dv-pulse-blue"}`}
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {open && (
        <div data-testid="ai-assistant-panel" className="fixed bottom-24 right-5 z-50 w-[calc(100vw-40px)] sm:w-[400px] h-[560px] max-h-[80vh] bg-white rounded-2xl border border-slate-200 shadow-2xl flex flex-col overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-5 py-4 text-white flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><Sparkles size={18} /></div>
            <div>
              <div className="font-semibold leading-tight">DealVest AI</div>
              <div className="text-xs text-blue-100">Your 24/7 advisor</div>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[14px] leading-relaxed ${m.role === "user" ? "bg-blue-600 text-white rounded-br-sm" : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"}`}>
                  {m.text || <span className="inline-flex gap-1"><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }}></span><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }}></span><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }}></span></span>}
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="border-t border-slate-200 p-3 bg-white flex gap-2" data-testid="assistant-form">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ask anything..."
              className="flex-1 h-11 px-4 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
              data-testid="assistant-input"
            />
            <button type="submit" disabled={streaming || !input.trim()} className="w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center disabled:opacity-40" data-testid="assistant-send">
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
